import React, {useEffect, useState} from "react";
import {Modal} from "react-bootstrap";
import {genericGetOne} from "../generic/crud";
import {ENTITY_ID_BRANCH, ENTITY_ID_DEALER, ENTITY_ID_USER, ENTITY_ID_ADVISORS} from "../generic/Constants";
import {toAbsoluteUrl} from "../../../_metronic/helpers";
import {getBrandImage} from "../generic/OEMList";

// Generate color from name hash
function getColorFromName(name) {
    const colors = [
        'primary',   // Blue
        'success',   // Green
        'info',      // Cyan
        'warning',   // Yellow/Orange
        'danger',    // Red
    ];

    // Improved hash function for better distribution
    let hash = 0;
    if (!name) return 'primary';

    for (let i = 0; i < name.length; i++) {
        const char = name.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32bit integer
    }

    const index = Math.abs(hash) % colors.length;
    return colors[index];
}

export function BranchDetailsDialog(props) {
    const {show, handleClose, branchId} = props;
    const [branch, setBranch] = useState(null);
    const [dealer, setDealer] = useState(null);
    const [manager, setManager] = useState(null);
    const [supervisors, setSupervisors] = useState([]);
    const [advisors, setAdvisors] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (branchId && show) {
            setIsLoading(true);

            // Fetch branch details
            genericGetOne(ENTITY_ID_BRANCH, branchId).then(
                ({data}) => {
                    setBranch(data);

                    // Fetch dealer details if dealerId exists
                    if (data.dealerId) {
                        genericGetOne(ENTITY_ID_DEALER, data.dealerId).then(
                            ({data: dealerData}) => {
                                setDealer(dealerData);
                            }
                        ).catch(() => {
                            setDealer(null);
                        });
                    }

                    // Fetch manager details if managerId exists
                    if (data.managerId) {
                        genericGetOne(ENTITY_ID_USER, data.managerId).then(
                            ({data: managerData}) => {
                                setManager(managerData);
                            }
                        ).catch(() => {
                            setManager(null);
                        });
                    }

                    // Fetch supervisor details if supervisorId exists
                    if (data.supervisorId && data.supervisorId.length > 0) {
                        const supervisorPromises = data.supervisorId.map(supervisorId =>
                            genericGetOne(ENTITY_ID_USER, supervisorId)
                                .then(({data: supervisorData}) => supervisorData)
                                .catch(() => null)
                        );

                        Promise.all(supervisorPromises).then(supervisorResults => {
                            setSupervisors(supervisorResults.filter(supervisor => supervisor !== null));
                        });
                    } else {
                        setSupervisors([]);
                    }

                    // Fetch advisor details if advisorIds exist
                    if (data.advisorIds && data.advisorIds.length > 0) {
                        const advisorPromises = data.advisorIds.map(advisorId =>
                            genericGetOne(ENTITY_ID_ADVISORS, advisorId)
                                .then(({data: advisorData}) => advisorData)
                                .catch(() => null)
                        );

                        Promise.all(advisorPromises).then(advisorResults => {
                            setAdvisors(advisorResults.filter(advisor => advisor !== null));
                        });
                    } else {
                        setAdvisors([]);
                    }

                    setIsLoading(false);
                })
                .catch(() => {
                    setIsLoading(false);
                });
        }
    }, [branchId, show]);

    if (!branch) {
        return null;
    }

    const brandImage = getBrandImage(dealer?.brand);
    const brandImageUrl = toAbsoluteUrl(`/media/car/${brandImage}`);

    return (
        <Modal
            show={show}
            onHide={handleClose}
            aria-labelledby="branch-details-modal"
            centered
        >
            <Modal.Header closeButton className="border-0 pb-0">
                <Modal.Title></Modal.Title>
            </Modal.Header>
            <Modal.Body className="pt-0 pb-10">
                {isLoading && (
                    <div className="text-center py-10">
                        <span className="spinner-border spinner-border-lg text-primary"></span>
                        <p className="mt-3 text-gray-500">Loading branch information...</p>
                    </div>
                )}

                {!isLoading && branch && (
                    <div className="px-2">
                        {/* Branch Profile Header */}
                        <div className="d-flex flex-column align-items-center mb-8">
                            <div className="symbol symbol-80px symbol-circle bg-light p-3 mb-4 shadow-sm border border-gray-200">
                                <img
                                    src={brandImageUrl}
                                    alt={dealer ? dealer.brand : 'Brand'}
                                    className="h-100 align-self-center object-fit-contain"
                                />
                            </div>
                            <h2 className="fw-bolder text-gray-900 mb-1">{branch.name}</h2>
                            {dealer && (
                                <div className="d-flex align-items-center mt-1 gap-2">
                                    <span className="badge badge-light-primary fw-bold px-3 py-2 fs-7">
                                        {dealer.name}
                                    </span>
                                    <span className="badge badge-light-info fw-bold px-3 py-2 fs-7 text-uppercase">
                                        {dealer.operatingCity || 'City N/A'}
                                    </span>
                                </div>
                            )}
                        </div>

                        {/* Branch Information List */}
                        <div className="mb-8">
                            <h5 className="mb-4 fw-bold text-gray-700">Staff Information</h5>
                            <div className="d-flex flex-column gap-3">
                                <div className="d-flex align-items-center justify-content-between p-3 rounded border border-gray-200 border-dashed">
                                    <div className="d-flex align-items-center">
                                        <i className="bi bi-person-badge fs-3 text-info me-3"></i>
                                        <span className="text-gray-600 fw-semibold">Manager</span>
                                    </div>
                                    <div className="text-end">
                                        <div className="text-gray-800 fw-bold">{branch.managerName || 'Not assigned'}</div>
                                        {manager && manager.phone && (
                                            <div className="text-gray-500 fs-7">{manager.phone}</div>
                                        )}
                                    </div>
                                </div>

                                <div className="d-flex align-items-start justify-content-between p-3 rounded border border-gray-200 border-dashed">
                                    <div className="d-flex align-items-center">
                                        <i className="bi bi-person-check fs-3 text-warning me-3"></i>
                                        <span className="text-gray-600 fw-semibold">Supervisors</span>
                                    </div>
                                    <div className="text-end">
                                        {supervisors.length === 0 ? (
                                            <div className="text-gray-800 fw-bold">Not assigned</div>
                                        ) : (
                                            <div className="d-flex flex-column gap-2">
                                                {supervisors.map((supervisor, index) => (
                                                    <div key={index}>
                                                        <div className="text-gray-800 fw-bold">{supervisor.name}</div>
                                                        {supervisor.phone && (
                                                            <div className="text-gray-500 fs-7">{supervisor.phone}</div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Service Advisors Section */}
                        <div className="separator separator-dashed my-6"></div>

                        <div>
                            <div className="d-flex align-items-center justify-content-between mb-4">
                                <h5 className="fw-bold text-gray-900 m-0">
                                    Service Advisors
                                    <span className="badge badge-circle badge-light-secondary ms-2 text-gray-600">
                                        {advisors.length}
                                    </span>
                                </h5>
                            </div>

                            {advisors.length === 0 ? (
                                <div className="text-center py-5 rounded bg-light border border-dashed border-gray-300">
                                    <i className="bi bi-info-circle fs-2 text-gray-400 mb-2 d-block"></i>
                                    <span className="text-muted fw-semibold">No advisors found</span>
                                </div>
                            ) : (
                                <div className="table-responsive" style={{maxHeight: '300px', overflowY: 'auto'}}>
                                    <table className="table table-row-bordered table-row-gray-200 align-middle gs-0 gy-3">
                                        <thead className="sticky-top bg-white" style={{zIndex: 1}}>
                                            <tr className="fw-bold text-muted">
                                                <th className="min-w-150px ps-3">Name</th>
                                                <th className="min-w-150px pe-3">Phone</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {advisors.map((advisor, index) => (
                                                <tr key={index} className={index % 2 !== 0 ? 'bg-light-primary' : ''}>
                                                    <td className="ps-3">
                                                        <div className="d-flex align-items-center">
                                                            <div className="symbol symbol-30px me-3">
                                                                <span className={`symbol-label bg-light-${getColorFromName(advisor.name)} text-${getColorFromName(advisor.name)} fw-bold`}>
                                                                    {advisor.name.charAt(0)}
                                                                </span>
                                                            </div>
                                                            <span className="text-gray-800 fw-bold fs-6">{advisor.name}</span>
                                                        </div>
                                                    </td>
                                                    <td className="pe-3">
                                                        <span className="text-gray-600 fw-semibold fs-7">
                                                            {advisor.phone || 'N/A'}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <button
                    type="button"
                    className="btn btn-light"
                    onClick={handleClose}>
                    Close
                </button>
            </Modal.Footer>
        </Modal>
    );
}
